import type { AdditionalWithholdingDto, SaveStateTaxInput, StateDraftDto } from '../types';

const MAX_AMOUNT = 9_999_999.99;

/** Parses a typed money amount ("1,250.5", "$20", "") into a number. Returns null for blank, NaN for anything that is not a plain amount. */
export function parseMoney(raw: string): number | null {
  const text = raw.replace(/[$,\s]/g, '');
  if (text === '') return null;
  if (!/^\d+(\.\d{0,2})?$/.test(text)) return Number.NaN;
  return Number(text);
}

/** An error message for a typed amount, or undefined when it is fine. A blank value is only an error when `required`. */
export function moneyError(raw: string, label: string, required: boolean): string | undefined {
  const n = parseMoney(raw);
  if (n === null) return required ? `${label} is required.` : undefined;
  if (Number.isNaN(n)) return `${label} must be an amount with at most two decimal places.`;
  if (n < 0 || n > MAX_AMOUNT) return `${label} must be between 0 and 9,999,999.99.`;
  return undefined;
}

/** A money value for an input (`125.5` shows as `125.50`; null shows blank). */
export const moneyText = (v: number | null | undefined): string => (v === null || v === undefined ? '' : v.toFixed(2));

/**
 * The state draft plus the voluntary additional-withholding request, as the ONE body the server takes. The server replaces both parts on
 * every save, so each screen that saves must send the other screen's saved values along or it would silently clear them.
 */
export function buildStateInput(draft: StateDraftDto, additional: AdditionalWithholdingDto): SaveStateTaxInput {
  return {
    stateCode: draft.stateCode ?? '',
    ...(draft.filingStatus ? { filingStatus: draft.filingStatus } : {}),
    ...(draft.allowances === null ? {} : { allowances: draft.allowances }),
    ...(draft.mostRecentWithholdingAmount === null ? {} : { mostRecentWithholdingAmount: draft.mostRecentWithholdingAmount }),
    ...(additional.state && additional.value !== null && additional.type === 'Amount'
      ? { additionalWithholdingState: additional.state, additionalWithholdingValue: additional.value, additionalWithholdingType: 'Amount' as const }
      : {}),
  };
}

export const ALLOWED_CERTIFICATE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
export const MAX_CERTIFICATE_BYTES = 10 * 1024 * 1024;

/** The same checks the server makes, so a bad file is caught before upload. The server still decides. */
export function certificateError(file: File): string | undefined {
  if (file.size <= 0) return 'The file is empty.';
  if (file.size > MAX_CERTIFICATE_BYTES) return 'The file is larger than 10 MB.';
  if (!ALLOWED_CERTIFICATE_TYPES.includes(file.type) || !/\.(pdf|png|jpe?g)$/i.test(file.name)) return 'Use a PDF, PNG or JPG file.';
  return undefined;
}
